// Submission Detail Modal Component

import { useState } from 'react';
import type { FormSubmission, SubmissionStatus } from '@/lib/types';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Select from '@/components/ui/Select';
import { submissionStatusColors, formTypeLabels } from '@/lib/types';
import { downloadSubmissionFile } from '@/lib/api';
import toast from 'react-hot-toast';

interface SubmissionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  submission: FormSubmission | null;
  onUpdateStatus?: (status: SubmissionStatus, notes?: string) => void;
  onAssign?: (submission: FormSubmission) => void;
  canEdit?: boolean;
}

export default function SubmissionDetailModal({
  isOpen,
  onClose,
  submission,
  onUpdateStatus,
  onAssign,
  canEdit = false,
}: SubmissionDetailModalProps) {
  const [status, setStatus] = useState<SubmissionStatus>('new');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  if (!submission) return null;

  // Reset bei neuem Submission
  if (isOpen && submission) {
    if (status !== submission.status) {
      setStatus(submission.status);
      setNotes(submission.notes || '');
    }
  }

  const handleDownloadText = () => {
    // Dateiname erstellen
    const formTypeLabel = formTypeLabels[submission.form_type];
    const customerName = submission.customer_name || 'Unzugeordnet';
    const date = new Date(submission.submitted_at).toISOString().split('T')[0];
    const fileName = `Formulareintrag_${formTypeLabel}_${customerName}_${date}.txt`
      .replace(/[^a-zA-Z0-9_.-]/g, '_'); // Sonderzeichen entfernen

    // Nur Formulardaten (Fragen & Antworten)
    let content = '';

    // Felder in richtiger Reihenfolge ausgeben
    const fields = submission.field_order && submission.field_order.length > 0
      ? submission.field_order.map(key => [key, submission.form_data[key]])
      : Object.entries(submission.form_data);

    fields.forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        content += `${key}:\n`;
        const valueStr = typeof value === 'object' 
          ? JSON.stringify(value, null, 2) 
          : String(value);
        content += valueStr;
        content += '\n\n';
      }
    });

    // Download auslösen
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Textdatei wurde heruntergeladen');
  };

  const handleSave = async () => {
    if (!onUpdateStatus) return;
    setIsSaving(true);
    try {
      await onUpdateStatus(status, notes);
      toast.success('Status aktualisiert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = async (fileId: string) => {
    try {
      const { url, filename } = await downloadSubmissionFile(fileId);
      // Download in neuem Tab öffnen
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.download = filename;
      a.click();
    } catch (error) {
      toast.error('Fehler beim Herunterladen');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Submission Details" size="lg">
      <div className="space-y-6">
        {/* Download Button (nur für Admins) */}
        {canEdit && (
          <div className="flex justify-end pb-4 border-b border-gray-200">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleDownloadText}
            >
              📄 Als Textdatei herunterladen
            </Button>
          </div>
        )}

        {/* Header Info */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
          <div>
            <div className="text-sm text-gray-500">Typ</div>
            <Badge variant="primary" className="mt-1">
              {formTypeLabels[submission.form_type]}
            </Badge>
          </div>
          <div>
            <div className="text-sm text-gray-500">Status</div>
            <Badge
              variant={
                submission.status === 'new' ? 'primary' :
                submission.status === 'completed' ? 'success' :
                submission.status === 'processing' ? 'warning' : 'secondary'
              }
              className="mt-1"
            >
              {submissionStatusColors[submission.status].label}
            </Badge>
          </div>
          <div>
            <div className="text-sm text-gray-500">Kunde</div>
            <div className="flex items-center gap-2">
              <div className="font-medium">{submission.customer_name || '-'}</div>
              {!submission.customer_id && onAssign && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onAssign(submission)}
                  className="text-xs"
                >
                  Zuweisen
                </Button>
              )}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Projekt</div>
            <div className="font-medium">{submission.project_name || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Eingereicht am</div>
            <div className="font-medium">
              {new Date(submission.submitted_at).toLocaleString('de-DE')}
            </div>
          </div>
          {submission.processed_at && (
            <div>
              <div className="text-sm text-gray-500">Bearbeitet am</div>
              <div className="font-medium">
                {new Date(submission.processed_at).toLocaleString('de-DE')}
              </div>
            </div>
          )}
        </div>

        {/* Form Data */}
        <div>
          <h3 className="text-lg font-medium mb-3">Formulardaten</h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  // Nutze field_order falls vorhanden, sonst Fallback
                  if (submission.field_order && submission.field_order.length > 0) {
                    // Exakte Formular-Reihenfolge verwenden
                    return submission.field_order.map((key) => {
                      const value = submission.form_data[key];
                      if (value === undefined) return null;
                      
                      return (
                        <tr key={key}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50 w-1/3">
                            {key}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                          </td>
                        </tr>
                      );
                    }).filter(Boolean);
                  } else {
                    // Fallback: Priority-basierte Sortierung
                    const priorityFields = [
                      'firmenname', 'name', 'ansprechpartner',
                      'email', 'telefon', 'phone',
                      'position', 'branche', 'interesse',
                      'zielgruppe', 'budget', 'nachricht', 'motivation',
                      'weitere_infos', 'weitere_anforderungen'
                    ];
                    
                    const entries = Object.entries(submission.form_data);
                    
                    const sorted = entries.sort((a, b) => {
                      const aIndex = priorityFields.indexOf(a[0].toLowerCase());
                      const bIndex = priorityFields.indexOf(b[0].toLowerCase());
                      
                      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
                      if (aIndex !== -1) return -1;
                      if (bIndex !== -1) return 1;
                      return 0;
                    });
                    
                    return sorted.map(([key, value]) => (
                      <tr key={key}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50 w-1/3">
                          {key}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                        </td>
                      </tr>
                    ));
                  }
                })()}
              </tbody>
            </table>
          </div>
        </div>

        {/* Files */}
        {submission.files && submission.files.length > 0 && (
          <div>
            <h3 className="text-lg font-medium mb-3">
              Dateien ({submission.files.length})
            </h3>
            <div className="space-y-2">
              {submission.files.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-3">
                    <svg
                      className="h-8 w-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    <div>
                      <div className="font-medium text-sm">{file.original_filename}</div>
                      <div className="text-xs text-gray-500">
                        {(file.file_size / 1024).toFixed(2)} KB • {file.mime_type}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDownload(file.id)}
                    className="text-primary-600 hover:text-primary-900"
                  >
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Status (nur für Admins) */}
        {canEdit && onUpdateStatus && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium mb-3">Bearbeitung</h3>
            <div className="space-y-4">
              <Select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value as SubmissionStatus)}
                options={[
                  { value: 'new', label: 'Neu' },
                  { value: 'processing', label: 'In Bearbeitung' },
                  { value: 'completed', label: 'Abgeschlossen' },
                  { value: 'archived', label: 'Archiviert' },
                ]}
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notizen
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="input"
                  placeholder="Interne Notizen..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="ghost" onClick={onClose}>
                  Abbrechen
                </Button>
                <Button onClick={handleSave} loading={isSaving}>
                  Speichern
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Read-only footer */}
        {!canEdit && (
          <div className="flex justify-end border-t border-gray-200 pt-4">
            <Button onClick={onClose}>
              Schließen
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
